import { useMemo, useState } from "react";
import {
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from "../../queries";
import type { Tag, CreateTagRequest, UpdateTagRequest } from "@/services/api/tags";
import { sortTags, type TagSortField } from "./tagUtils";
import { useTagsSearchParams } from "./useTagsSearchParams";

export function useTagsPanel() {
  const { data: tags, isLoading } = useTags();
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();
  const deleteMutation = useDeleteTag();

  const { sorting, updateSearchParams } = useTagsSearchParams();
  const { sortField, sortDirection } = sorting;

  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [deletingTag, setDeletingTag] = useState<Tag | null>(null);

  const sortedTags = useMemo<Tag[]>(() => {
    if (!tags) return [];
    return sortTags(tags, sortField, sortDirection);
  }, [tags, sortField, sortDirection]);

  const handleSort = (field: TagSortField) => {
    if (sortField === field) {
      updateSearchParams({
        sortField: field,
        sortDirection: sortDirection === "asc" ? "desc" : "asc",
      });
    } else {
      updateSearchParams({
        sortField: field,
        sortDirection: "asc",
      });
    }
  };

  const handleCreate = (data: CreateTagRequest) => {
    if (!data.name) return;

    createMutation.mutate(data);
  };

  const handleUpdate = (id: string | number, data: UpdateTagRequest) => {
    updateMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          setEditingTag(null);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deletingTag) return;

    deleteMutation.mutate(deletingTag.id, {
      onSuccess: () => {
        setDeletingTag(null);
      },
    });
  };

  return {
    tags: sortedTags,
    isLoading,
    sortField,
    sortDirection,
    editingTag,
    deletingTag,
    handleSort,
    handleCreate,
    handleUpdate,
    handleDelete,
    setEditingTag,
    setDeletingTag,
    createIsLoading: createMutation.isPending,
    updateIsLoading: updateMutation.isPending,
    deleteIsLoading: deleteMutation.isPending,
    tagsCount: tags?.length,
  };
}


